/**
 * LandingTemplatesPanel — 只读模板 / 开关 / 文案键（ADM-LP-S003）
 *
 * 数据只来自 GET /landing-templates。生成器未接入时 fail-closed，不写死模板。
 */

import {
  useLandingPagesPage,
  useLandingTemplates,
  useRetryLandingLocaleCopy,
} from '~/data/queries';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { LangChip } from '~/ui/LangChip';
import { Section } from '~/ui/Section';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';

export default function LandingTemplatesPanel() {
  const { data, isLoading, isError } = useLandingTemplates();
  const retryCopy = useRetryLandingLocaleCopy();
  const role = useAuthStore((state) => state.user?.role);
  const canRetry = role === 'root' || role === 'admin' || role === 'operator';
  const tasksQ = useLandingPagesPage({ page: 1, pageSize: 200 });

  if (isLoading) {
    return (
      <div
        className="card"
        style={{ padding: 24, color: 'var(--fg-muted)', fontSize: 'var(--text-base)' }}
      >
        加载模板清单…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div
        className="card"
        style={{ padding: 24, color: 'var(--neg)', fontSize: 'var(--text-base)' }}
      >
        模板清单不可用（novel-campaign-pages 未接入或代理失败）。任务表单不会写死模板候选。
      </div>
    );
  }

  const copyKeys = data.copy?.keys ?? [];
  const defaults = data.copy?.defaults ?? {};
  const langs = Object.keys(defaults);

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div className="card" style={{ padding: '12px 14px', background: 'var(--bg-subtle)' }}>
        <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
          <Icons.IconInfo size={13} style={{ color: 'var(--fg-muted)', marginTop: 2 }} />
          <div style={{ fontSize: 'var(--text-xs)', lineHeight: 1.6 }}>
            本页<span style={{ fontWeight: 500 }}>只读</span>：模板清单 / 开关 /
            文案键与各语言系统默认值全部来自{' '}
            <span className="mono">GET /api/v1/landing-templates</span>。admin
            不硬编码模板、不维护键表或默认文案。
          </div>
        </div>
      </div>

      {data.copy_provisioning && data.copy_provisioning.length > 0 && (
        <Section
          title="语言默认文案准备"
          sub="开启书籍语言后自动准备，通常在两分钟内开始；仅补缺失语言，已有文案保持不变。"
        >
          <Table className="tbl">
            <thead>
              <tr>
                <th>语言</th>
                <th>状态</th>
                <th>说明</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {data.copy_provisioning.map((item) => (
                <tr key={item.language}>
                  <td>
                    <LangChip code={item.language} />
                  </td>
                  <td>
                    {
                      {
                        ready: '已就绪',
                        pending: '等待准备',
                        processing: '准备中',
                        failed: '准备失败',
                      }[item.status]
                    }
                  </td>
                  <td
                    style={{ color: item.status === 'failed' ? 'var(--neg)' : 'var(--fg-muted)' }}
                  >
                    {item.reason ??
                      (item.status === 'ready'
                        ? '完整默认文案已可用'
                        : '后台将自动补齐对应语种文案')}
                    {item.next_retry_at &&
                      `；自动重试时间：${new Date(item.next_retry_at).toLocaleString()}`}
                  </td>
                  <td>
                    {canRetry && item.status === 'failed' && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={retryCopy.isPending}
                        onClick={() =>
                          retryCopy.mutate(item.language, {
                            onSuccess: () => toast('已排队准备该语言文案'),
                            onError: (error) =>
                              toast(error instanceof Error ? error.message : '文案准备失败', {
                                tone: 'neg',
                              }),
                          })
                        }
                      >
                        重新准备
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Section>
      )}

      <Section
        title="模板清单 templates[]"
        sub="needs_image=true 的模板：任务必须传对应素材，前端提交前校验"
      >
        <Table className="tbl">
          <thead>
            <tr>
              <th>template_id</th>
              <th>展示名</th>
              <th>needs_image</th>
              <th>说明</th>
              <th>使用中任务</th>
            </tr>
          </thead>
          <tbody>
            {data.templates.map((tp) => (
              <tr key={tp.id}>
                <td className="mono" style={{ fontSize: 'var(--text-xs)' }}>
                  {tp.id}
                </td>
                <td style={{ fontSize: 'var(--text-sm)' }}>{tp.name ?? tp.id}</td>
                <td>
                  {tp.needs_image ? (
                    <span className="chip chip-warn" style={{ fontSize: 'var(--text-2xs)' }}>
                      true · 必传素材
                    </span>
                  ) : (
                    <span className="chip" style={{ fontSize: 'var(--text-2xs)' }}>
                      false
                    </span>
                  )}
                </td>
                <td style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
                  {tp.desc ?? ''}
                </td>
                <td className="mono" style={{ fontSize: 'var(--text-xs)' }}>
                  {tasksQ.data.filter((t) => t.template_id === tp.id).length}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>

      {data.options && data.options.length > 0 && (
        <Section
          title="开关清单 options[]"
          sub="requires 列出开启所需字段 · 不满足时任务表单禁用该开关"
        >
          <Table className="tbl">
            <thead>
              <tr>
                <th>option</th>
                <th>展示名</th>
                <th>requires</th>
                <th>说明</th>
              </tr>
            </thead>
            <tbody>
              {data.options.map((o, i) => (
                <tr key={o.id ?? String(i)}>
                  <td className="mono" style={{ fontSize: 'var(--text-xs)' }}>
                    {o.id}
                  </td>
                  <td style={{ fontSize: 'var(--text-sm)' }}>{o.name}</td>
                  <td>
                    {(o.requires ?? []).map((r) => (
                      <span
                        key={r}
                        className="chip mono"
                        style={{ fontSize: 'var(--text-2xs)', marginRight: 4 }}
                      >
                        {r}
                      </span>
                    ))}
                  </td>
                  <td style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
                    {o.desc ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Section>
      )}

      {copyKeys.length > 0 && (
        <Section
          title="文案键 copy · 键闭集与各语言系统默认值"
          sub="页脚隐私 / 条款 / 版权由生成器 locale 固定提供，不在可编辑键内 · 任务提交整份 copy_overrides"
        >
          <div style={{ overflowX: 'auto' }}>
            <Table
              className="tbl"
              style={{
                tableLayout: 'fixed',
                width: 160 + langs.length * 240,
                minWidth: 160 + langs.length * 240,
              }}
            >
              <colgroup>
                <col style={{ width: 160 }} />
                {langs.map((lang) => (
                  <col key={lang} style={{ width: 240 }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th>copy key</th>
                  {langs.map((l) => (
                    <th key={l}>
                      <LangChip code={l} style={{ fontSize: 'var(--text-2xs)' }} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {copyKeys.map((k) => (
                  <tr key={k.key}>
                    <td
                      style={{
                        fontSize: 'var(--text-xs)',
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      <div className="mono" style={{ fontWeight: 500 }}>
                        {k.key}
                      </div>
                      {k.name && (
                        <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-xs)' }}>
                          {k.name}
                        </div>
                      )}
                      {k.scope && (
                        <span
                          className="chip mono"
                          style={{ fontSize: 'var(--text-2xs)', marginTop: 2 }}
                        >
                          {k.scope}
                        </span>
                      )}
                    </td>
                    {langs.map((l) => (
                      <td
                        key={l}
                        style={{
                          fontSize: 'var(--text-xs)',
                          color: 'var(--fg-muted)',
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {defaults[l]?.[k.key] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Section>
      )}
    </div>
  );
}
